import { useEffect, useRef } from "react";

// Left-rail topic navigator for the vertical item list. Lists the test's
// topics, highlights whichever section the viewer is currently looking at
// (driven by the scroll-spy in use-active-topic), and clicking one smoothly
// scrolls the page to that topic's section.

interface TopicNavigatorProps {
  topics: { id: string; name: string }[];
  activeId?: string;
  onSelect: (id: string) => void;
  /** Optional per-topic item counts, keyed by topic id. */
  counts?: Record<string, number>;
  className?: string;
}

export function TopicNavigator({ topics, activeId, onSelect, counts, className = "" }: TopicNavigatorProps) {
  const listRef = useRef<HTMLUListElement>(null);

  // When the active topic changes (from the page scroll-spy) and the list is
  // tall enough to scroll, keep the active item in view by nudging only the
  // list's own scroll — never the page. Aligns to whichever edge it's hidden
  // past: scrolling down brings the active topic to the bottom (last visible),
  // scrolling up brings it to the top (first visible). Already-visible: no-op.
  useEffect(() => {
    const container = listRef.current;
    if (!container || !activeId) return;
    const activeEl = container.querySelector<HTMLElement>(
      `[data-testid="topic-nav-${activeId}"]`,
    );
    if (!activeEl) return;
    const c = container.getBoundingClientRect();
    const el = activeEl.getBoundingClientRect();
    if (el.top < c.top) {
      container.scrollTop -= c.top - el.top;
    } else if (el.bottom > c.bottom) {
      container.scrollTop += el.bottom - c.bottom;
    }
  }, [activeId]);

  return (
    <nav className={`flex flex-col min-h-0 ${className}`} aria-label="Test topics">
      <p className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 px-3">
        Topics
      </p>
      {/* The list itself scrolls when there are more topics than fit, so the
          options/Finalize card below it in the rail is never pushed offscreen. */}
      <ul ref={listRef} className="space-y-1 min-h-0 overflow-y-auto">
        {topics.map((t) => {
          const active = t.id === activeId;
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onSelect(t.id)}
                className={`flex w-full items-start gap-1 rounded-md px-3 py-2 text-left text-sm leading-snug transition-colors ${
                  active
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                aria-current={active ? "true" : undefined}
                data-testid={`topic-nav-${t.id}`}
              >
                <span className="line-clamp-2 flex-1 min-w-0">{t.name}</span>
                {counts && (
                  <span className="text-xs opacity-70 tabular-nums">{counts[t.id] ?? 0}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
